import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

/**
 * CompanyContext
 *
 * Source of truth for companyId comes from `/users/{uid}.companyId`.
 * (This is required by the published Firestore rules which check membership via users/{uid}.companyId)
 *
 * Also exposes the company's configured IANA `timezone` (e.g. "America/New_York")
 * so surfaces like /today can anchor "today" to the business's operating timezone
 * rather than the tech's device timezone.
 */
const CompanyContext = createContext();

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within a CompanyProvider');
  return ctx;
};

const _deviceTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
};

export const CompanyProvider = ({ children }) => {
  const { user, profile, isAuthLoading } = useAuth();

  const companyId = profile?.companyId || null;
  const [companyTimezone, setCompanyTimezone] = useState(null);

  // Subscribe to the company doc for the `timezone` field. Keep the subscription
  // narrow — we only need one field, but `onSnapshot` on the full doc is still
  // cheap and future-proof if we add more company-level fields.
  useEffect(() => {
    if (!companyId || !db) { setCompanyTimezone(null); return undefined; }
    const ref = doc(db, 'companies', companyId);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : null;
      const tz = (data?.timezone || '').trim() || null;
      setCompanyTimezone(tz);
    }, () => setCompanyTimezone(null));
    return () => unsub();
  }, [companyId]);

  const value = useMemo(
    () => {
      const deviceTimezone = _deviceTz();
      const effectiveTimezone = companyTimezone || deviceTimezone;
      return {
        companyId,
        isCompanyLoading: isAuthLoading || (!!user && !companyId),
        companyTimezone,      // raw, may be null
        deviceTimezone,       // always resolved
        effectiveTimezone,    // prefer company, fall back to device
      };
    },
    [companyId, isAuthLoading, user, companyTimezone]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export default CompanyContext;
